// MA-10 PII scrub, as a LEAF module (moved verbatim from sentry.ts by
// MA-1044, which re-exports both functions). No imports: the readiness
// check's lean entry uses it without pulling in the app's Sentry setup.
// ─────────────────────────────────────────────────────────────────────────
// MA-10 — PII scrub (beforeSend / beforeBreadcrumb).
//
// This is a kids' app: no child name, age, birthday, email, or free-text
// answer may ever reach Sentry. The capture helpers below forward an
// open-ended `context` object into Sentry `extra` (e.g. captureSupabaseError
// dumps `{ label, ...context }`), so a future caller could pass a child's name
// without anyone noticing. These hooks are the safety net: every outgoing
// event and breadcrumb is walked and known-PII values are redacted.
//
// DESIGN (ADR-006):
//  • CONSERVATIVE DENY-LIST, exact lowercased key match (NOT substring) — so
//    `childId`/`moduleName`/`context` (opaque or debug-valuable) survive while
//    `childName`/`name`/`age`/`email` are redacted. Substring matching is a
//    trap: "context" contains "text"; "childId" contains "child".
//  • REDACT to "[redacted]" rather than delete — keeps the signal that a field
//    existed (debug value) and makes the test assertion crisp.
//  • Walk extra / contexts / tags / request.data / breadcrumbs[].data; drop
//    `event.user` wholesale (the app sets no Sentry user by design — see the
//    module header).
//  • NEVER return null from scrubEvent — returning null from beforeSend DROPS
//    the event, re-blinding prod (the exact failure this module exists to
//    prevent). Wrapped in try/catch with a still-sendable fallback.
//  • Depth cap + WeakSet cycle guard so a self-referential or huge `extra`
//    cannot blow the stack or hang the pipeline.
//
// KNOWN GAPS (out of scope for key-based scrubbing — documented, not hidden):
//  • PII interpolated into FREE STRINGS (error.message, event.message,
//    breadcrumb.message, componentStack) is NOT caught — redacting those
//    heuristically would break Sentry's error grouping. The control there is
//    upstream discipline: don't interpolate child text/names into messages.
//  • Session Replay travels a different pipeline; its PII control is the
//    existing replayIntegration({ maskAllText, blockAllMedia }) config below,
//    not this scrubber.

/** Sentinel written in place of a redacted value. */
const PII_REDACTED = "[redacted]";

/** Max recursion depth when walking nested objects/arrays. App `context`
 * objects are shallow; this only bounds pathological inputs. */
const PII_MAX_DEPTH = 6;

/** Known PII field names (lowercased, EXACT match). Every entry is a field the
 * app actually collects (child name, age band, birthday parts, email, free-text
 * answer). Add a key here when a new PII field is introduced. */
const PII_KEYS = new Set<string>([
  "name", "childname", "child_name", "studentname", "student_name",
  "displayname", "display_name", "fullname", "full_name",
  "firstname", "first_name", "lastname", "last_name",
  "age", "ageband", "age_band",
  "birth_month", "birthmonth", "birth_year", "birthyear", "birthday", "dob",
  "email", "emailaddress", "email_address",
  "answer", "childanswer", "child_answer",
  "childtext", "child_text", "freetext", "free_text", "textanswer", "text_answer",
]);

function isPiiKey(key: string): boolean {
  return PII_KEYS.has(key.toLowerCase());
}

/** Recursively redact PII-keyed values in place. Returns the same reference for
 * objects/arrays (mutated); returns primitives unchanged. Guards cycles (seen)
 * and excessive depth. */
function scrubInPlace(value: unknown, depth: number, seen: WeakSet<object>): unknown {
  if (value === null || typeof value !== "object") return value;
  if (seen.has(value as object)) return value; // cycle — already walked
  if (depth > PII_MAX_DEPTH) return value;
  seen.add(value as object);

  if (Array.isArray(value)) {
    for (let i = 0; i < value.length; i++) {
      value[i] = scrubInPlace(value[i], depth + 1, seen);
    }
    return value;
  }

  const obj = value as Record<string, unknown>;
  for (const key of Object.keys(obj)) {
    if (isPiiKey(key)) {
      obj[key] = PII_REDACTED;
    } else {
      obj[key] = scrubInPlace(obj[key], depth + 1, seen);
    }
  }
  return obj;
}

/** Redact known-PII keys across one event container (extra/contexts/tags/...). */
function scrubContainer(container: unknown): void {
  if (container && typeof container === "object") {
    scrubInPlace(container, 0, new WeakSet<object>());
  }
}

/**
 * beforeSend hook: strip known PII from an outgoing Sentry event. PURE and
 * init-free so it is unit-testable under node/tsx (initSentry can't run there).
 * MUST NOT return null (that would drop the event). Defensive: any internal
 * failure falls back to an event with the free-form PII containers removed.
 */
export function scrubEvent<T>(event: T): T {
  if (!event || typeof event !== "object") return event;
  try {
    const e = event as unknown as Record<string, unknown>;
    scrubContainer(e.extra);
    scrubContainer(e.contexts);
    scrubContainer(e.tags);
    const req = e.request as Record<string, unknown> | undefined;
    if (req && typeof req === "object") scrubContainer(req.data);
    // The app sets no Sentry user by design (module header). Drop any user
    // object wholesale — id/email/username/ip_address are all identity.
    if ("user" in e) delete (e as any).user;
    // Events can carry their own breadcrumb trail.
    if (Array.isArray(e.breadcrumbs)) {
      e.breadcrumbs = e.breadcrumbs.map((b) => scrubBreadcrumb(b));
    }
    return event;
  } catch {
    // Never throw inside Sentry's pipeline and never return null. Strip the
    // free-form PII vectors and return a still-sendable event.
    try {
      const e = event as any;
      delete e.extra; delete e.contexts; delete e.user; delete e.request;
    } catch {
      /* ignore */
    }
    return event;
  }
}

/**
 * beforeBreadcrumb hook: strip known PII from a breadcrumb's `data`. PURE /
 * init-free for testability. Leaves category/level/message (developer-authored
 * constants) intact. Returns the (scrubbed) breadcrumb — never throws.
 */
export function scrubBreadcrumb<T>(breadcrumb: T): T {
  if (!breadcrumb || typeof breadcrumb !== "object") return breadcrumb;
  try {
    scrubContainer((breadcrumb as unknown as Record<string, unknown>).data);
  } catch {
    /* ignore — keep the breadcrumb */
  }
  return breadcrumb;
}

